import { useState } from 'react';
import { Play } from 'lucide-react';
import { useReviewMedia } from '@/hooks/useReviewMedia';
import { storageThumbUrl } from '@/lib/imageThumb';
import ReviewMediaLightbox from './ReviewMediaLightbox';

interface ReviewMediaHeroProps {
  reviewId: string;
  className?: string;
}

/**
 * Elevated single-preview hero for media-rich reviews in feeds.
 * Shows the first photo or video poster at a larger aspect ratio.
 * Replaces the inline thumbnail gallery in non-compact ReviewCard layout.
 */
const ReviewMediaHero = ({ reviewId, className = '' }: ReviewMediaHeroProps) => {
  const { data: items = [], isLoading } = useReviewMedia(reviewId);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  if (isLoading || items.length === 0) return null;

  const hero = items[0];
  const extra = items.length - 1;
  const isVideo = hero.media_type === 'video';
  const poster = isVideo ? hero.thumbnail_url || undefined : hero.media_url;

  const open = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setActiveIndex(0);
    setLightboxOpen(true);
  };

  return (
    <>
    <button
      type="button"
      onPointerDown={(e) => e.stopPropagation()}
      onClick={open}
      aria-label={isVideo ? 'Play review video' : 'Open review photo'}
      className={`relative mt-3 block w-full aspect-[16/10] overflow-hidden rounded-xl bg-muted ring-1 ring-border/40 text-left ${className}`}
    >
      {isVideo ? (
        <>
          {poster ? (
            <img
              src={storageThumbUrl(poster, { width: 640 })}
              alt=""
              loading="lazy"
              decoding="async"
              className="absolute inset-0 w-full h-full object-cover"
            />
          ) : (
            <video
              src={hero.media_url}
              className="absolute inset-0 w-full h-full object-cover"
              muted
              playsInline
              preload="metadata"
            />
          )}
          <div className="absolute inset-0 flex items-center justify-center bg-black/15">
            <div className="rounded-full bg-black/55 backdrop-blur-sm p-3">
              <Play className="h-5 w-5 text-white fill-white" />
            </div>
          </div>
        </>
      ) : (
        <img
          src={storageThumbUrl(hero.media_url, { width: 640 })}
          alt=""
          loading="lazy"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover"
        />
      )}
      {extra > 0 && (
        <div className="absolute top-2.5 right-2.5 px-2 py-0.5 rounded-full bg-black/55 text-white text-[11px] backdrop-blur-sm">
          +{extra}
        </div>
      )}
    </button>
      <ReviewMediaLightbox
        open={lightboxOpen}
        onOpenChange={setLightboxOpen}
        media={items[activeIndex]}
        onPrev={() => setActiveIndex((p) => (p > 0 ? p - 1 : items.length - 1))}
        onNext={() => setActiveIndex((p) => (p < items.length - 1 ? p + 1 : 0))}
        currentIndex={activeIndex}
        totalCount={items.length}
      />
    </>
  );
};

export default ReviewMediaHero;
